import { useState, useRef, useEffect } from 'react';

const SLIDES = [
  {
    title: 'Your Vehicles, Organized',
    icon: (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D3474A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
    ),
    bullets: [
      'Log miles and maintenance across all vehicles',
      'Cloud backup — never lose service records',
      'Access from any device, anywhere',
    ],
  },
  {
    title: 'Smart Insights',
    icon: (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D3474A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a7 7 0 017 7c0 2.38-1.19 4.47-3 5.74V17a1 1 0 01-1 1H9a1 1 0 01-1-1v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 017-7z" />
        <line x1="9" y1="21" x2="15" y2="21" />
      </svg>
    ),
    bullets: [
      'Real-time market valuations (not outdated book values)',
      'Research vehicles before purchase',
      'Verify repair quotes — don\'t get ripped off',
      'Diagnose issues with expert guidance',
    ],
  },
  {
    title: 'Individuals & Businesses',
    icon: (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D3474A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87" />
        <path d="M16 3.13a4 4 0 010 7.75" />
      </svg>
    ),
    columns: [
      {
        label: 'Car Enthusiasts',
        items: ['Track your collection', 'Plan maintenance', 'Know what your cars are worth'],
      },
      {
        label: 'Businesses',
        items: ['Manage customer vehicles', 'Share with your team', 'Professional service tracking'],
      },
    ],
  },
];

export default function StepCarousel({ onNext }) {
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef(null);

  // Auto-advance every 4 seconds, loops back to start
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev < SLIDES.length - 1 ? prev + 1 : 0));
    }, 4000);
    return () => clearInterval(timer);
  }, [current]);

  function handlePointerDown(e) {
    touchStartX.current = e.clientX;
  }

  function handlePointerUp(e) {
    if (touchStartX.current === null) return;
    const diff = e.clientX - touchStartX.current;
    if (Math.abs(diff) > 50) {
      if (diff < 0 && current < SLIDES.length - 1) setCurrent(current + 1);
      else if (diff > 0 && current > 0) setCurrent(current - 1);
    }
    touchStartX.current = null;
  }

  const slide = SLIDES[current];

  return (
    <div className="screen" style={{ justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
      {/* Logo at top */}
      <div style={{ paddingTop: 24, marginBottom: 8 }}>
        <img
          src="/logo-tall.png"
          alt="Proper Miles"
          style={{ height: 64, objectFit: 'contain', margin: '0 auto' }}
        />
      </div>

      {/* Slide content */}
      <div
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          touchAction: 'pan-y',
          userSelect: 'none',
          width: '100%',
          maxWidth: 500,
          margin: '0 auto',
        }}
      >
        {/* Icon */}
        <div
          style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'var(--accent-subtle)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 24,
          }}
        >
          {slide.icon}
        </div>

        {/* Title */}
        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: 20, color: 'var(--text-primary)' }}>
          {slide.title}
        </h1>

        {/* Bullets or columns */}
        {slide.bullets && (
          <ul style={{ textAlign: 'left', margin: '0 auto', maxWidth: 320, padding: 0, listStyle: 'none' }}>
            {slide.bullets.map((b, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12, fontSize: '1.0625rem', color: 'var(--text-secondary)' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {b}
              </li>
            ))}
          </ul>
        )}

        {slide.columns && (
          <div style={{ display: 'flex', gap: 16, width: '100%', maxWidth: 360 }}>
            {slide.columns.map((col, ci) => (
              <div key={ci} style={{ flex: 1, textAlign: 'left' }}>
                <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
                  {col.label}
                </div>
                <ul style={{ padding: 0, listStyle: 'none', margin: 0 }}>
                  {col.items.map((item, i) => (
                    <li key={i} style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 8, paddingLeft: 14, position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 0, color: 'var(--accent)' }}>•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dot indicators */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 24 }}>
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            style={{
              width: i === current ? 24 : 8,
              height: 8,
              borderRadius: 4,
              background: i === current ? 'var(--accent)' : 'var(--border)',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              padding: 0,
            }}
          />
        ))}
      </div>

      {/* CTA */}
      <button className="btn btn-primary" onClick={onNext} style={{ marginBottom: 16 }}>
        Start Free — 1 Vehicle, No Credit Card
      </button>
      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
        No payment required. Upgrade anytime.
      </p>
    </div>
  );
}
